import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import useBranches from "hooks/useBranches";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { fetchMinistries } from "routes";
import { API_BASE, API_HOST } from "config/api";

// Define prop types for row data
const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  churchName: PropTypes.string,
  ministryName: PropTypes.string,
  status: PropTypes.string,
  createdAt: PropTypes.string,
  originalData: PropTypes.object,
});

// Define individual Cell components with PropTypes
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = {
  value: PropTypes.any.isRequired,
};

// Create a factory function for ChurchCell that receives the church object
const createChurchCell = (church) => {
  const ChurchCell = ({ value }) => (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{value?.charAt(0)}</Avatar>
      <Box>
        <Typography variant="button" fontWeight={600} display="block">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {church?.email || "church@example.com"}
        </Typography>
      </Box>
    </Box>
  );
  ChurchCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  return ChurchCell;
};

const MinistryCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Created: {new Date(row.original.createdAt).toLocaleDateString()}
    </Typography>
  </Box>
);
MinistryCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
};

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: rowPropTypes,
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function useMinistryData() {
  const { user, church } = useAuth();
  const { branches, canFilterByBranch } = useBranches();
  const [branchFilter, setBranchFilter] = useState(null);

  // Create ChurchCell with access to the church object
  const ChurchCell = createChurchCell(church);

  const [ministryData, setMinistryData] = useState({
    columns: [
      {
        Header: "SN",
        accessor: "serial",
        align: "center",
        width: "5%",
        Cell: SerialCell,
      },
      {
        Header: "Church",
        accessor: "churchName",
        align: "left",
        width: "30%",
        Cell: ChurchCell,
      },
      {
        Header: "Ministry",
        accessor: "ministryName",
        align: "left",
        width: "25%",
        Cell: MinistryCell,
      },
      {
        Header: "Status",
        accessor: "status",
        align: "center",
        width: "15%",
        Cell: StatusCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState(null);

  const handleEdit = (ministry) => {
    setSelectedMinistry(ministry);
    setEditModalOpen(true);
  };

  const handleDelete = (ministry) => {
    setSelectedMinistry(ministry);
    setDeleteModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedMinistry.id
        ? `${API_BASE}/ministries/${selectedMinistry.id}`
        : `${API_BASE}/ministries`;

      const method = selectedMinistry.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: selectedMinistry.ministryName,
          status: selectedMinistry.status === "active",
          church_id: church.id,
          branch_id: selectedMinistry.branchId || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Show success message
      Swal.fire({
        title: "Success!",
        text: `Ministry successfully saved`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchMinistries();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Ministry operation failed:", error);
      setEditModalOpen(false);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      //   setMinistryData((prev) => ({
      //     ...prev,
      //     error: error.message,
      //   }));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/ministries/${selectedMinistry.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Ministry, ${selectedMinistry.ministryName} deleted successfully`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Refresh the data
      fetchMinistries();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      setMinistryData((prev) => ({
        ...prev,
        error: error.message,
      }));
    }
  };

  const handleAddMinistry = () => {
    setSelectedMinistry({
      id: null,
      ministryName: "",
      status: "active",
      branchId: null,
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchMinistries = useCallback(async () => {
    try {
      setMinistryData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      // Authenticated, branch-scoped endpoint — NOT /ministries/church/{id},
      // which is the public unauthenticated route used by the registration
      // form and ignores branch scoping entirely.
      const url = branchFilter
        ? `${API_BASE}/ministries?branch_id=${branchFilter}`
        : `${API_BASE}/ministries`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const ministries = result.data || result.ministries || result;

      if (!Array.isArray(ministries)) {
        throw new Error("API response is not an array");
      }

      setMinistryData((prev) => ({
        ...prev,
        rows: ministries.map((min, index) => ({
          id: min.id,
          serial: index + 1,
          churchName: church.name,
          ministryName: min.name || min.ministry_name,
          status: min.status === "active" || min.status === true ? "active" : "inactive",
          createdAt: min.created_at,
          branchId: min.branch_id,
          originalData: min,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setMinistryData((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  }, [user, church, branchFilter]);

  useEffect(() => {
    fetchMinistries();
  }, [fetchMinistries]);

  return {
    ...ministryData,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMinistry,
    setSelectedMinistry,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMinistry,
    branches,
    canFilterByBranch,
    branchFilter,
    setBranchFilter,
  };
}
